import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Paper, Grid, Alert, CircularProgress, Chip, Stack, Divider } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { getCurrentUser, updateUser } from '../api/services/userService/User';
import { useUser } from '../../contexts/UserContext';
import { AdminPanelSettings, Business, Groups, Group } from '@mui/icons-material';

export default function Settings() {
  const { keycloak } = useKeycloak();
  const { getUserRoles, isSuperAdmin } = useUser();
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    apiKey: '',
    huggingfaceToken: '',
    kaggleToken: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchCurrentUser = async () => {
    try {
      await keycloak.updateToken(30);
      const response = await getCurrentUser(keycloak.token);
      if (response.success) {
        const user = response.user;
        setCurrentUser(user);
        setProfile(prev => ({
          ...prev,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          company: user.company || '',
          position: user.position || '',
          huggingfaceToken: user.huggingfaceToken || prev.huggingfaceToken,
          kaggleToken: user.kaggleToken || prev.kaggleToken,
          apiKey: user.apiKey || prev.apiKey
        }));
      } else {
        setError('Failed to load user information');
        console.error('Failed to fetch current user:', response.error);
      }
    } catch (error) {
      setError('Error loading user information');
      console.error('Error fetching current user:', error);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak.authenticated) {
      fetchCurrentUser();
    } else {
      setUserLoading(false);
    }
  }, [keycloak.authenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await keycloak.updateToken(30);
      if (!currentUser?.id) {
        setError('User ID not found. Please refresh the page.');
        setLoading(false);
        return;
      }
      const userData = {
        id: currentUser.id,
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        company: profile.company,
        position: profile.position,
        status: currentUser.status || 0
      };
      const response = await updateUser(userData, keycloak.token);
      if (response.success) {
        setSuccess('Settings updated successfully');
        await fetchCurrentUser();
      } else {
        setError(response.message || 'Failed to update settings');
      }
    } catch (err) {
      console.error('Update user error:', err);
      setError('Failed to update settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading user information...</Typography>
      </Box>
    );
  }

  const roleIcons = {
    global: <AdminPanelSettings fontSize="small" />,
    client: <Business fontSize="small" />,
    department: <Groups fontSize="small" />,
    team: <Group fontSize="small" />
  };

  return (
    <Box sx={{ maxWidth: "100%", mx: 'auto', px: { xs: 2, sm: 3 }, pt: 2, pb: { xs: 2, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Settings
        </Typography>
        
        {getUserRoles && getUserRoles().length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
              Your Roles
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {getUserRoles().map((role, idx) => (
                <Chip
                  key={idx}
                  icon={roleIcons[role.type]}
                  label={role.label}
                  size="medium"
                  sx={{
                    fontWeight: 500,
                    bgcolor: role.type === 'global' ? 'error.main' : 'primary.main',
                    color: 'white',
                    '& .MuiChip-icon': {
                      color: 'inherit'
                    }
                  }}
                />
              ))}
            </Stack>
            <Divider sx={{ mt: 3 }} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: { xs: 2, sm: 3 } }}>{success}</Alert>}
        
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Profile Information
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={profile.firstName}
                onChange={(e) => setProfile({...profile, firstName: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={profile.lastName}
                onChange={(e) => setProfile({...profile, lastName: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({...profile, email: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={profile.phone}
                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={profile.company}
                onChange={(e) => setProfile({...profile, company: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                value={profile.position}
                onChange={(e) => setProfile({...profile, position: e.target.value})}
                size={window.innerWidth < 600 ? "small" : "medium"}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 3 }, 
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mt: { xs: 2, sm: 3 }
              }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  size={window.innerWidth < 600 ? "medium" : "large"}
                  fullWidth={window.innerWidth < 600}
                  sx={{
                    minWidth: { xs: 'auto', sm: '120px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    py: { xs: 1.5, sm: 2 }
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
